import firebase from '../firebase';
import { countAllowanceSessions } from './testerName';

/**
 * Session limit mode configuration
 * - 'total': Maximum sessions stored at any time (user can delete to get under limit)
 * - 'monthly': Sessions per month with automatic reset
 */
export const SESSION_LIMIT_MODE = 'total';

const SESSION_LIMITS = {
  regular: 3,
  basic: 5,
  solo: 10,
  pro: 20
};

const GRACE_PERIODS = {
  regular: 7,
  basic: 7,
  solo: 30,
  pro: 30
};

/**
 * Get current month string in YYYY-MM-01 format
 */
function getCurrentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
}

/**
 * Get user testing usage for a user, resetting if it's a new month
 * @param {string} userId - The user ID
 * @returns {Promise<{sessionsUsedThisMonth: number, lastResetDate: string|null, totalSessionsEver: number}>}
 */
export async function getUserTestingUsage(userId) {
  const db = firebase.firestore();
  const userRef = db.collection('user').doc(userId);
  const userDoc = await userRef.get();

  if (!userDoc.exists) {
    return { sessionsUsedThisMonth: 0, lastResetDate: null, totalSessionsEver: 0 };
  }

  const data = userDoc.data();
  const usage = data.userTestingUsage || {
    sessionsUsedThisMonth: 0,
    lastResetDate: null,
    totalSessionsEver: 0
  };

  const currentMonth = getCurrentMonth();

  // Check if we need to reset (new month)
  if (usage.lastResetDate !== currentMonth) {
    // Reset counter for new month
    await userRef.update({
      'userTestingUsage.sessionsUsedThisMonth': 0,
      'userTestingUsage.lastResetDate': currentMonth
    });
    return {
      sessionsUsedThisMonth: 0,
      lastResetDate: currentMonth,
      totalSessionsEver: usage.totalSessionsEver || 0
    };
  }

  return usage;
}

/**
 * Increment the session count for a user
 * @param {string} userId - The user ID
 */
export async function incrementSessionCount(userId) {
  const db = firebase.firestore();
  const userRef = db.collection('user').doc(userId);
  const currentMonth = getCurrentMonth();

  // Use set with merge to handle case where userTestingUsage doesn't exist yet
  await userRef.set({
    userTestingUsage: {
      sessionsUsedThisMonth: firebase.firestore.FieldValue.increment(1),
      totalSessionsEver: firebase.firestore.FieldValue.increment(1),
      lastResetDate: currentMonth
    }
  }, { merge: true });
}

/**
 * Get the session limit for a subscription type
 * @param {string} subscriptionType - The subscription type
 * @returns {number} - The session limit
 */
export function getSessionLimit(subscriptionType) {
  return SESSION_LIMITS[subscriptionType] ?? SESSION_LIMITS.regular;
}

/**
 * Count total testing sessions stored for a specific project
 * Used when SESSION_LIMIT_MODE is 'total'
 * @param {string} userId - The owner's user ID
 * @param {string} projectId - The project's generatedUrl
 * @returns {Promise<number>} - Total session count
 */
export async function getTotalSessionCount(userId, projectId) {
  const db = firebase.firestore();
  try {
    const snapshot = await db
      .collection('user')
      .doc(userId)
      .collection('url')
      .doc(projectId)
      .collection('testingSessions')
      .get();
    // The owner's own "Preview as tester" runs are free (testerName.js).
    return countAllowanceSessions(snapshot);
  } catch (error) {
    console.error('Error counting sessions:', error);
    return 0;
  }
}

/**
 * Count total testing sessions across ALL projects for a user
 * Used when SESSION_LIMIT_MODE is 'total'
 * @param {string} userId - The owner's user ID
 * @returns {Promise<number>} - Total session count across all projects
 */
export async function getTotalSessionCountAllProjects(userId) {
  const db = firebase.firestore();
  try {
    // Get all project URLs for this user
    const urlsSnapshot = await db
      .collection('user')
      .doc(userId)
      .collection('url')
      .get();

    // Count sessions in each project
    let totalCount = 0;
    const countPromises = urlsSnapshot.docs.map(async (urlDoc) => {
      const sessionsSnapshot = await urlDoc.ref
        .collection('testingSessions')
        .get();
      // The owner's own "Preview as tester" runs are free (testerName.js).
      return countAllowanceSessions(sessionsSnapshot);
    });

    const counts = await Promise.all(countPromises);
    totalCount = counts.reduce((sum, count) => sum + count, 0);

    return totalCount;
  } catch (error) {
    console.error('Error counting all sessions:', error);
    return 0;
  }
}

/**
 * Get the grace period in days for a subscription type
 * @param {string} subscriptionType - The subscription type
 * @returns {number} - Grace period in days
 */
export function getGracePeriod(subscriptionType) {
  return GRACE_PERIODS[subscriptionType] || 7;
}

/**
 * Check if a user can create a new session (synchronous version)
 * Use for monthly mode or when you already have the session count
 * @param {string} subscriptionType - The subscription type
 * @param {number} sessionsUsed - Number of sessions used (monthly or total depending on mode)
 * @returns {{allowed: boolean, limit: number, used: number, remaining: number}}
 */
export function canCreateSession(subscriptionType, sessionsUsed) {
  const limit = getSessionLimit(subscriptionType);
  return {
    allowed: sessionsUsed < limit,
    limit,
    used: sessionsUsed,
    remaining: Math.max(0, limit - sessionsUsed)
  };
}

/**
 * Check if a user can create a new session (async version for 'total' mode)
 * This checks the actual session count from the database
 * @param {string} subscriptionType - The subscription type
 * @param {string} userId - The owner's user ID
 * @returns {Promise<{allowed: boolean, limit: number, used: number, remaining: number}>}
 */
export async function canCreateSessionAsync(subscriptionType, userId) {
  const limit = getSessionLimit(subscriptionType);

  if (SESSION_LIMIT_MODE === 'total') {
    const totalSessions = await getTotalSessionCountAllProjects(userId);
    return {
      allowed: totalSessions < limit,
      limit,
      used: totalSessions,
      remaining: Math.max(0, limit - totalSessions)
    };
  } else {
    // Monthly mode - use existing usage tracking
    const usage = await getUserTestingUsage(userId);
    return {
      allowed: usage.sessionsUsedThisMonth < limit,
      limit,
      used: usage.sessionsUsedThisMonth,
      remaining: Math.max(0, limit - usage.sessionsUsedThisMonth)
    };
  }
}

/**
 * Check if a user can access the User Testing feature
 * @param {string} subscriptionType - The subscription type
 * @param {boolean} trialExpired - Whether the trial has expired
 * @returns {{allowed: boolean, reason?: string}}
 */
export function canAccessUserTesting(subscriptionType, trialExpired) {
  // If user is on regular (free) plan and trial has expired, deny access
  if (subscriptionType === 'regular' && trialExpired) {
    return { allowed: false, reason: 'trial_expired' };
  }

  // All tiers can access the feature
  return { allowed: true };
}

/**
 * Get the next tier name for upgrade prompts
 * @param {string} currentTier - The current subscription type
 * @returns {string} - The next tier name
 */
export function getNextTierName(currentTier) {
  const tiers = ['regular', 'basic', 'solo', 'pro'];
  const currentIndex = tiers.indexOf(currentTier);

  if (currentIndex < 0 || currentIndex >= tiers.length - 1) {
    return 'Solo';
  }

  const nextTier = tiers[currentIndex + 1];
  return nextTier.charAt(0).toUpperCase() + nextTier.slice(1);
}

/**
 * Get the date string for the 1st of next month
 * @returns {string} - Formatted date string (e.g., "February 1st")
 */
export function getNextResetDate() {
  const now = new Date();
  const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);

  const month = nextMonth.toLocaleDateString('en-US', { month: 'long' });
  const day = nextMonth.getDate();
  const suffix = day === 1 ? 'st' : day === 2 ? 'nd' : day === 3 ? 'rd' : 'th';

  return `${month} ${day}${suffix}`;
}
